import axios from 'axios';
import { toast } from 'react-toastify';

// Flag to prevent duplicate account deletion toasts
let isAccountDeletedToastShown = false;

const DELETED_ACCOUNT_ERROR_MESSAGE = 'Your account has been deleted. Please contact support.';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create a global axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a response interceptor to handle 401 and 403 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // Handle 401 Unauthorized - logout for auth errors only
      if (status === 401 && data?.message) {
        localStorage.removeItem('access_token');
        sessionStorage.setItem('login_error', data.message);
        window.location.href = '/login';
      }

      // Handle 403 Forbidden - Account deleted scenario
      // Check for the specific account deletion message
      if (status === 403 && data?.message === DELETED_ACCOUNT_ERROR_MESSAGE) {
        // Prevent duplicate toasts if multiple API calls fail simultaneously
        if (!isAccountDeletedToastShown) {
          isAccountDeletedToastShown = true;

          // Show toast with the error message
          toast.error(DELETED_ACCOUNT_ERROR_MESSAGE, {
            position: 'top-right',
            autoClose: 5000, // Show toast for 5 seconds before auto-closing
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
            onClose: () => {
              // Reset the flag when toast is closed
              isAccountDeletedToastShown = false;
            },
          });

          // Delay redirect to allow user to see the toast message
          setTimeout(() => {
            // Clear all user session/auth data
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            sessionStorage.clear();

            // Redirect to login page
            window.location.href = '/login';
          }, 3000); // Redirect after 3 seconds to let user read the message
        } else {
          // If toast already shown, still clear session and redirect but faster
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          sessionStorage.clear();
          window.location.href = '/login';
        }
      }

      // Handle 409 Conflict - but don't logout for business logic conflicts
      // These are duplicate collaborator errors, not auth errors
      // Let the error propagate to the component for proper handling
    }

    return Promise.reject(error);
  }
);


// Helper for authenticated requests
export function getAuthHeaders(token) {
  const authToken = token || localStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };
}

export function apiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

// Reminder API functions for Assets
export async function fetchReminders(userId, assetId) {
  const response = await api.get(`/reminders?userId=${userId}&assetId=${assetId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

// Fetch all reminders for a user
export async function fetchAllReminders(userId) {
  const response = await api.get(`/reminders?userId=${userId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

// Reminder API functions for Liabilities
export async function fetchRemindersForLiability(userId, liabilityId) {
  const response = await api.get(`/reminders?userId=${userId}&liabilityId=${liabilityId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function createReminder(reminderData) {
  const response = await api.post('/reminders', reminderData, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function updateReminder(reminderId, reminderData) {
  const response = await api.patch(`/reminders/${reminderId}`, reminderData, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function deleteReminder(reminderId) {
  const response = await api.delete(`/reminders/${reminderId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function completeReminder(reminderId) {
  const response = await api.post(`/reminders/${reminderId}/complete`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

// Global search API function
export async function globalSearch(query, page = 1, limit = 100) {
  const response = await api.get(`/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

// Example usage:
// api.get('/user', { headers: getAuthHeaders() })
// api.post('/user', data, { headers: getAuthHeaders() })
